<template>
	<header
		class="page-header"
		:class="{
			'page-header--width-full': widthFull,
			'page-header--reserve-vertical-space': reserveVerticalSpace,
			'page-header--has-no-description': !hasDescription,
		}"
	>
		<content-placeholders v-if="dummy" animated class="header__placeholders">
			<p class="header__heading">
				<content-placeholders-text :lines="1" />
			</p>

			<p v-if="hasDescription" class="page-header__description">
				<content-placeholders-text :lines="3" />
			</p>
		</content-placeholders>

		<template v-else>
			<h2 class="page-header__heading">
				<slot>
					{{ heading }}
				</slot>
			</h2>

			<p v-if="hasDescription" class="page-header__description">
				<slot name="description">
					{{ description }}
				</slot>
			</p>

			<div v-if="hasButtons" class="page-header__buttons">
				<slot name="buttons" />
			</div>
		</template>
	</header>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { useHasSlot } from '@/composables';

export default defineComponent({
	props: {
		heading: {
			type: String,
			default: '',
		},

		reserveVerticalSpace: {
			type: Boolean,
			default: true,
		},

		widthFull: {
			type: Boolean,
			default: false,
		},

		description: {
			type: String,
			default: null,
		},

		dummy: {
			type: Boolean,
			default: false,
		},
	},

	setup(props, { slots }) {
		return {
			hasDescription: useHasSlot('description', slots, hasSlot => hasSlot || !!props.description),
			hasButtons: useHasSlot('buttons', slots),
		};
	},
});
</script>

<style lang="scss">
.page-header {
	$self: &;

	max-width: 38rem;

	@include max-tablet-portrait {
		display: grid;
		grid-template-columns: 5fr 7fr;
		max-width: 100%;
	}

	&--has-no-description {
		@include max-tablet-portrait {
			display: block;
		}
	}

	&__heading {
		@include heading-1;
		display: flex;
		align-items: flex-end;

		@include max-tablet-portrait {
			align-items: flex-start;
			padding-right: 2.2rem;
		}

		.vue-content-placeholders-text {
			padding: 0.65rem 0;
		}

		.vue-content-placeholders-text__line {
			height: 4.5rem;
		}
	}

	&__description {
		margin-top: 2.4rem;
		color: $color-gray;
		font-weight: 500;
		line-height: 1.28;

		@include max-tablet-portrait {
			margin-top: 0;
		}
	}

	&__buttons {
		.base-button {
			animation: animation-name-auto('in') 300ms forwards $easeOutQuad;

			&:first-child {
				margin-top: 4rem;
			}

			&:not(:last-child) {
				margin-bottom: 2.4rem;
			}

			@keyframes #{animation-name-auto('in')} {
				from {
					transform: translate(20px);
					opacity: 0;
				}

				to {
					transform: translateX(0);
					opacity: 1;
				}
			}
		}
	}

	&--width-full {
		max-width: 100%;
		.page-header__heading {
			min-height: auto !important;
		}
	}

	&--reserve-vertical-space {
		#{$self}__heading {
			min-height: 2 * percent-size($font-size-h1, 120%);

			@include max-lg-and-portrait {
				min-height: auto;
			}
		}
	}

	.vue-content-placeholders-text__line {
		background: $color-medium;
	}
}
</style>
